import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  useTheme,
  Button,
  Tooltip
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Delete } from "@mui/icons-material";
import { tokens } from '../../theme';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import { useNavigate, useParams } from "react-router-dom";
import useStudentService from '../../api/services/studentService';
import useTermService from '../../api/services/termService';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import useClassService from "../../api/services/ClassService";
import DeleteDialog from "./Delete_Student";
import CreateDialog from "./Create_Student";
import GenerateIndexNumberDialog from "./generateIndexNumberDialog";
import CreateNewAccountDetails from "../Accountant/CreateNewAccountDetails";
import RectorsMessageDialog from "../RectorComment/RectorsMessageDialog";
import StudentEvaluationDialog from "../StudentEvaluation/StudentEvaluationDialog";
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

const ClassStudents = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id: classId } = useParams();
  const navigate = useNavigate();
  const {
    fetchStudentsByClassId,
    deleteStudent,
    students,
    generateStudentsNumberByClassId,
  } = useStudentService();

  const { changeToGraduateStatus } = useClassService();
  const { fetchAllActiveTerms, terms } = useTermService();

  // Get user role from cookies
  const user = getUserFromCookies();
  const userRole = user?.data?.role;
  const isAdmin = userRole === 'ADMIN';
  const isSecretary = userRole === 'SECRETARY';
  const isAccountant = userRole === 'ACCOUNTANT';
  const isClassTeacher = userRole === 'CLASSTEACHER';
  const isChief = userRole === 'CHIEF';
  const isAcademic = userRole === 'ACADEMIC';
  const isManager = userRole === 'MANAGER';

  const [showAllStudents, setShowAllStudents] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountsDialogOpen, setAccountsDialogOpen] = useState(false);
  const [rectorDialogOpen, setRectorDialogOpen] = useState(false);
  const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
  const [generateIndexDialogOpen, setGenerateIndexDialogOpen] = useState(false);

  const loadStudents = async () => {
    if (classId) {
      setLoading(true);
      const statusFilter = showAllStudents ? null : "ACTIVE";
      await fetchStudentsByClassId(classId, statusFilter);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [classId, showAllStudents]);

  useEffect(() => {
    if (isAdmin || isSecretary || isAcademic || isChief || isManager) {
      fetchAllActiveTerms();
    }
  }, [isAdmin, isSecretary, isAcademic, isChief, isManager]);

  const handleDeleteClick = (studentId) => {
    setSelectedStudentId(studentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedStudentId(null);
    setShowAllStudents(false);
    loadStudents();
  };

  const confirmDeleteStudent = () => {
    if (selectedStudentId) {
      deleteStudent(selectedStudentId, () => {
        setShowAllStudents(false);
        handleDeleteDialogClose();
      });
    }
  };

  const openStudentDetailsPage = (studentId) => {
    navigate(`/student_details/${studentId}`);
  };

  const viewParentsOfStudent = (studentId) => {
    navigate(`/student_parents/${studentId}`);
  };

  const openStudentAccountsDetailsPage = (studentId) => {
    navigate(`/students-accounts-details/${studentId}`);
  };

  const openStudentRectorcommentsDetailsPage = (studentId) => {
    navigate(`/student-rector-comments/${studentId}`);
  };

  const openStudentTermEvaluationReportPage = (studentId) => {
    navigate(`/student-term-evaluation-data/${studentId}`);
  };



  const handleAllUsersClick = () => {
    setShowAllStudents(!showAllStudents);
  };

  const openCreateDialog = () => {
    setIsDialogOpen(true);
  };

  const handleGenerateIndexNumbers = () => {
    setGenerateIndexDialogOpen(true);
  };

  const handleGenerateIndexDialogClose = () => {
    setGenerateIndexDialogOpen(false);
  };

  const handleGenerateIndexSuccess = () => {
    setGenerateIndexDialogOpen(false);
    loadStudents();
  };

  const handleChangeStatus = async () => {
    try {
      setLoading(true);
      await changeToGraduateStatus(classId);
      await loadStudents();
    } catch (error) {
      console.error(" Failed to change status of students and class", error);
    } finally {
      setLoading(false);
    }
  };

  const sortedStudents = useMemo(() => {
    const filteredStudents = showAllStudents
      ? students
      : students.filter(student => student.status === 'ACTIVE');

    return [...filteredStudents].sort((a, b) => {
      const combCompare = a.combination.localeCompare(b.combination, undefined, { sensitivity: "base" });
      if (combCompare !== 0) return combCompare;
      return a.studentName.localeCompare(b.studentName, undefined, { sensitivity: "base" });
    });
  }, [students, showAllStudents]);

  const selectedStudent = selectedStudentId ? students.find(s => s.id === selectedStudentId) : null;

  // Debug logging
  console.log("Current state:", {
    isSecretary,
    rectorDialogOpen,
    evaluationDialogOpen,
    selectedStudentId,
    selectedStudent,
    terms: terms?.length || 0,
    students: students?.length || 0
  });

  const columns = [
    {
      field: "studentName",
      headerName: "Student Name",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "combination",
      headerName: "Combination",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "studentNumber",
      headerName: "Student Number",
      flex: 1,
      sortable: true,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: true,
    },
    ...((isAccountant || isAcademic || isAdmin || isChief || isManager) ? [{
      field: "accounts",
      headerName: "Accounts",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={() => openStudentAccountsDetailsPage(row.id)}
          >
            ACCOUNTS
          </Button>
        </Box>
      ),
    }] : []),
    ...((isAcademic || isAdmin || isChief || isManager || isSecretary ) ? [{
      field: "rector",
      headerName: "Rector",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={() => openStudentRectorcommentsDetailsPage(row.id)}
          >
            RECTOR
          </Button>
        </Box>
      ),
    }] : []),
    ...((isSecretary || isAcademic || isAdmin || isChief || isManager || isClassTeacher) ? [{
      field: "secretary",
      headerName: "Secretary",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={() => openStudentTermEvaluationReportPage(row.id)}
          >
            EVALUATION
          </Button>
        </Box>
      ),
    }] : []),
    {
      field: "Parents",
      headerName: "Parents",
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" width="100%" mt={1}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={() => viewParentsOfStudent(row.id)}
          >
            PARENTS
          </Button>
        </Box>
      ),
    },
    ...((isAdmin || isChief || isManager || isAcademic) ? [{
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center">
          <Tooltip title="Delete Student">
            <IconButton color="secondary" onClick={() => handleDeleteClick(row.id)}>
              <Delete style={{ color: "red" }} />
            </IconButton>
          </Tooltip>
          <IconButton color="success" onClick={() => openStudentDetailsPage(row.id)}>
            <VisibilityOutlinedIcon style={{ color: "green" }} />
          </IconButton>
        </Box>
      ),
    }] : []),
  ];

  return (
    <Box m="0px">
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={confirmDeleteStudent}
        studentId={selectedStudentId}
      />

      <CreateDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={loadStudents}
        clazzId={classId}
      />

      <GenerateIndexNumberDialog
        open={generateIndexDialogOpen}
        onClose={handleGenerateIndexDialogClose}
        onSuccess={handleGenerateIndexSuccess}
        classId={classId}
        generateStudentsNumberByClassId={generateStudentsNumberByClassId}
      />

      {(isAdmin || isChief || isManager || isAcademic || isAccountant) && selectedStudent && (
        <CreateNewAccountDetails
          open={accountsDialogOpen}
          onClose={() => {
            setAccountsDialogOpen(false);
            setSelectedStudentId(null);
          }}
          onSuccess={() => {
            setAccountsDialogOpen(false);
            setSelectedStudentId(null);
          }}
          studentId={selectedStudent.id}
          studentName={selectedStudent.studentName}
        />
      )}

      {(isAdmin || isChief || isManager || isAcademic || isSecretary) && (
        <RectorsMessageDialog
          open={rectorDialogOpen}
          onClose={() => {
            console.log("Closing Rector dialog");
            setRectorDialogOpen(false);
            setSelectedStudentId(null);
          }}
          students={students}
          terms={terms}
          selectedStudent={selectedStudent}
        />
      )}

      {(isAdmin || isChief || isManager || isAcademic || isSecretary) && (
        <StudentEvaluationDialog
          open={evaluationDialogOpen}
          onClose={() => {
            console.log("Closing Student Evaluation dialog");
            setEvaluationDialogOpen(false);
            setSelectedStudentId(null);
          }}
          students={students}
          terms={terms}
          selectedStudent={selectedStudent}
        />
      )}

      <Box p={2} ml={2} mr={2}>
        <Header title="STUDENTS" />

        {(isAdmin || isChief || isAcademic || isManager) && (
          <Box mb={2}>
            <Button
              color="secondary"
              variant="contained"
              style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px" }}
              onClick={handleAllUsersClick}
            >
              {showAllStudents ? "ACTIVE ONLY" : "ALL STUDENTS"}
            </Button>
            <Button
              color="secondary"
              variant="contained"
              style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px" }}
              onClick={openCreateDialog}
            >
              ADD STUDENTS
            </Button>
            <Button
              color="secondary"
              variant="contained"
              style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px", marginTop: "5px" }}
              onClick={handleGenerateIndexNumbers}
            >
              GENERATE INDEX NUMBER
            </Button>
            <Button
              color="secondary"
              variant="contained"
              style={{ padding: "5px 8px", fontSize: "13px", marginRight: "10px", marginTop: "5px" }}
              onClick={handleChangeStatus}
            >
              GRADUATE STATUS
            </Button>
          </Box>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <Box height="72vh">
            <DataGrid
              rows={sortedStudents}
              columns={columns}
              disableRowSelectionOnClick
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              getRowId={(row) => row.id}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClassStudents;
